<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import UserBar from '@/js/core/components/parts/user-bar/UserBar.vue';
import NavigationPanel from '@/js/core/components/parts/navigation-panel/NavigationPanel.vue';
import CoursesFilters from '@/js/core/components/parts/courses/CoursesFilters.vue';
import CoursesViewAlphabetical from '@/js/core/components/parts/courses/CoursesViewAlphabetical.vue';
import CoursesViewUpcoming from '@/js/core/components/parts/courses/CoursesViewUpcoming.vue';
import {
  buildCoursesFilterGroups,
  coursesFilterGroups,
  withFacetDisabledOptions,
} from '@/js/core/utils/courses-filter-groups.js';
import {
  coursesQueryFromFilters,
  normalizeCourse,
  normalizeUpcomingGroups,
} from '@/js/core/utils/courses.js';
import { parseCoursesState } from '@/js/core/utils/courses-url-state.js';
import { useCoursesUrlState } from '@/js/core/composables/useCoursesUrlState.js';
import { useCoursesStore } from '@/js/core/stores/courses-store';
import { useCurrentPageStore } from '@/js/core/stores/current-page-store';
import { useLoadingStore } from '@/js/core/stores/loading-store';
import { useTimezoneStore } from '@/js/core/stores/timezone-store';
import PageHeading from '@/js/core/components/parts/PageHeading.vue';

const views = {
  alphabetical: CoursesViewAlphabetical,
  upcoming: CoursesViewUpcoming,
};

// Seed view + filters from the URL so deep links / bookmarks restore the saved state.
const initialUrlState = parseCoursesState(window.location.search);

const activeView = ref(initialUrlState.view || Object.keys(views)[0]);
const renderedView = ref(activeView.value);

const selectedFilters = ref(
  Object.fromEntries(
    coursesFilterGroups.map(group => [group.id, initialUrlState.filters[group.id] || ['all']])
  )
);

useCoursesUrlState({ activeView, selectedFilters });

const coursesStore = useCoursesStore();
const currentPageStore = useCurrentPageStore();
const loadingStore = useLoadingStore();
const timezoneStore = useTimezoneStore();

const filterGroups = computed(() =>
  withFacetDisabledOptions(
    buildCoursesFilterGroups(coursesStore.filters),
    coursesStore.filters?.facets,
    selectedFilters.value
  )
);
const alphabeticalCourses = computed(() =>
  coursesStore.courses.map(course => normalizeCourse(course, timezoneStore.effectiveTimezone))
);
const upcomingGroups = computed(() =>
  normalizeUpcomingGroups(coursesStore.upcomingSessions, timezoneStore.effectiveTimezone)
);
const visibleCourses = computed(() =>
  renderedView.value === 'upcoming' ? upcomingGroups.value : alphabeticalCourses.value
);
const errorMessage = computed(() => {
  if (coursesStore.filtersError) return coursesStore.filtersError;

  return renderedView.value === 'upcoming' ? coursesStore.upcomingError : coursesStore.coursesError;
});
const hasVisibleResults = computed(() => {
  if (renderedView.value === 'upcoming') {
    return upcomingGroups.value.some(group => group.sessions.length > 0);
  }
  return alphabeticalCourses.value.length > 0;
});

onMounted(() => {
  timezoneStore.hydrateFromCurrentProfile();
});

// The store fetches are no-ops when the filters are unchanged and page 1 is loaded.
async function refresh(view) {
  const fetch = view === 'upcoming' ? coursesStore.fetchUpcoming : coursesStore.fetchCourses;

  loadingStore.start();
  try {
    await fetch({ query: coursesQueryFromFilters(selectedFilters.value) });
  } finally {
    loadingStore.complete();
  }
}

watch(activeView, async view => {
  await refresh(view);
  renderedView.value = view;
});

watch(selectedFilters, () => refresh(renderedView.value), { deep: true });

const hasMore = computed(() =>
  renderedView.value === 'upcoming' ? coursesStore.hasMoreUpcoming : coursesStore.hasMoreCourses
);

const handleLoadMore = () =>
  renderedView.value === 'upcoming'
    ? coursesStore.fetchMoreUpcoming()
    : coursesStore.fetchMoreCourses();
</script>

<template>
  <UserBar />
  <NavigationPanel />
  <main id="main-content" class="courses-page_main-content">
    <section id="page-content" class="courses-page">
      <header class="courses-page_header">
        <PageHeading
          :title="currentPageStore.title || 'Courses'"
          :description="currentPageStore.infoContent"
        />
      </header>

      <CoursesFilters
        v-model:view="activeView"
        v-model:filters="selectedFilters"
        :views="views"
        :filter-groups="filterGroups"
      />

      <div class="courses-page-content">
        <div v-if="errorMessage">{{ errorMessage }}</div>
        <component
          :is="views[renderedView]"
          v-else
          :courses="visibleCourses"
          :has-more="hasMore"
          @load-more="handleLoadMore"
        />
      </div>

      <div v-if="hasVisibleResults" class="courses-timezone-footer">
        <p>
          All courses are displayed in {{ timezoneStore.effectiveTimezoneLabel }} time.
          <a href="/profile">Visit your profile</a> to change the time zone settings.
        </p>
      </div>
    </section>
  </main>
</template>
